import React, { FC, Fragment } from 'react';
import styled from '@emotion/styled';
import { Grid } from '$components/layouts';
import { useTranslation } from '$hooks';
import { IEnrichedProductSerie, IProduct } from '$models';
import { Link } from '$components/elements/link';
import { isTruthyNumber } from '$lib/helpers';

type Props = {
    product?: IProduct;
    series?: IEnrichedProductSerie[];
};

export const NonBookDetails: FC<Props> = ({ product, series }: Props): JSX.Element => {
    const { translate } = useTranslation();

    if (!product) return <></>;

    return (
        <StyledGrid flow="row" columns="1fr 1fr">
            {!!series?.length && (
                <>
                    <div>
                        {series.length === 1
                            ? translate('productPage.productDescription.seriesLabelSingle')
                            : translate('productPage.productDescription.seriesLabel')}
                    </div>
                    <div>
                        {series.map((serie: IEnrichedProductSerie, index: number) => (
                            <Fragment key={index}>
                                <Link themedLink href={serie?.url}>
                                    {serie?.title}
                                </Link>
                                {index < (series?.length || 0) - 1 ? (
                                    <span>{` ${translate('productPage.productDescription.and')} `}</span>
                                ) : (
                                    <></>
                                )}
                            </Fragment>
                        ))}
                    </div>
                </>
            )}
            {product.itemNumber && (
                <>
                    <div>{translate('productPage.productDescription.itemNumberLabel')}</div>
                    <div>{product.itemNumber}</div>
                </>
            )}
            {product.itemEanCode && (
                <>
                    <div>{translate('productPage.productDescription.itemEanCodeLabel')}</div>
                    <div>{product.itemEanCode}</div>
                </>
            )}
            {product.productBrand && (
                <>
                    <div>{translate('productPage.productDescription.productBrandLabel')}</div>
                    <div>{product.productBrand}</div>
                </>
            )}
            {product.itemGroupName && (
                <>
                    <div>{translate('productPage.productDescription.itemGroupNameLabel')}</div>
                    <div>{product.itemGroupName}</div>
                </>
            )}
            {product.itemReleaseDate && (
                <>
                    <div>{translate('productPage.productDescription.itemReleaseDateLabel')}</div>
                    <div>
                        {product.itemReleaseDate.length > 10
                            ? product.itemReleaseDate.slice(0, 10)
                            : product.itemReleaseDate}
                    </div>
                </>
            )}
            {product.itemColorMain && (
                <>
                    <div>{translate('productPage.productDescription.itemColorMainLabel')}</div>
                    <div>{product.itemColorMain}</div>
                </>
            )}
            {product.nonBookDetails?.itemPattern && (
                <>
                    <div>{translate('productPage.productDescription.itemPatternLabel')}</div>
                    <div>{product.nonBookDetails?.itemPattern}</div>
                </>
            )}
            {product.nonBookDetails?.itemFormatConcatenated && (
                <>
                    <div>{translate('productPage.productDescription.itemFormatConcatinatedLabel')}</div>
                    <div>{product.nonBookDetails?.itemFormatConcatenated}</div>
                </>
            )}
            {product.nonBookDetails?.itemSize && (
                <>
                    <div>{translate('productPage.productDescription.itemSizeLabel')}</div>
                    <div>{product.nonBookDetails?.itemSize}</div>
                </>
            )}
            {product.nonBookDetails?.itemPackageSize && (
                <>
                    <div>{translate('productPage.productDescription.itemPackageSizeLabel')}</div>
                    <div>{product.nonBookDetails?.itemPackageSize}</div>
                </>
            )}
            {product.nonBookDetails?.itemHardness && (
                <>
                    <div>{translate('productPage.productDescription.itemHardnessLabel')}</div>
                    <div>{product.nonBookDetails?.itemHardness}</div>
                </>
            )}
            {product.nonBookDetails?.itemThickness && (
                <>
                    <div>{translate('productPage.productDescription.itemThicknessLabel')}</div>
                    <div>{product.nonBookDetails?.itemThickness}</div>
                </>
            )}
            {product.nonBookDetails?.productAgeGroup && (
                <>
                    <div>{translate('productPage.productDescription.productAgeGroupLabel')}</div>
                    <div>{product.nonBookDetails?.productAgeGroup}</div>
                </>
            )}
            {product.nonBookDetails?.productNumberOfParticipants && (
                <>
                    <div>{translate('productPage.productDescription.productNumberOfParticipantsLabel')}</div>
                    <div>{product.nonBookDetails?.productNumberOfParticipants}</div>
                </>
            )}
            {product.nonBookDetails?.productPlayingTime && (
                <>
                    <div>{translate('productPage.productDescription.productPlayingTimeLabel')}</div>
                    <div>{product.nonBookDetails?.productPlayingTime}</div>
                </>
            )}
            {product.nonBookDetails?.productBatteryType && (
                <>
                    <div>{translate('productPage.productDescription.productBatterieTypeLabel')}</div>
                    <div>{product.nonBookDetails?.productBatteryType}</div>
                </>
            )}
            {product.nonBookDetails?.productBatteryQuantity && (
                <>
                    <div>{translate('productPage.productDescription.productBatteryQuantityLabel')}</div>
                    <div>{product.nonBookDetails?.productBatteryQuantity}</div>
                </>
            )}
            {isTruthyNumber(product.itemHeight) && (
                <>
                    <div>{translate('productPage.productDescription.itemHeightLabel')}</div>
                    <div>{product.itemHeight}</div>
                </>
            )}
            {isTruthyNumber(product.itemWidth) && (
                <>
                    <div>{translate('productPage.productDescription.itemWidthLabel')}</div>
                    <div>{product.itemWidth}</div>
                </>
            )}
            {isTruthyNumber(product.itemLength) && (
                <>
                    <div>{translate('productPage.productDescription.itemLengthLabel')}</div>
                    <div>{product.itemLength}</div>
                </>
            )}
            {isTruthyNumber(product.itemWeight) && (
                <>
                    <div>{translate('productPage.productDescription.itemWeightLabel')}</div>
                    <div>{product.itemWeight}</div>
                </>
            )}
        </StyledGrid>
    );
};

const StyledGrid = styled(Grid)(({ theme }) => ({
    '& div': {
        fontSize: theme.fontSizes.sm,
        borderBottom: `1px solid ${theme.colors.primaryGreyLight}`,
        padding: `${theme.space[3]} 0`,
        ':first-child, :nth-child(2)': {
            paddingTop: 0,
        },
    },

    '& div:nth-child(even)': {
        textAlign: 'right',
    },
}));
